/**
 * Industry taxonomy — UK SIC 2007 + NAICS → human-readable categories.
 *
 * Mirrors backend/app/services/industry_taxonomy.py. Keep the two in sync
 * when adding codes or buckets — mismatched chips/filters cause silent
 * over- or under-matching against the backend query.
 */

export interface IndustryCategory {
  key: string;
  label: string;
}

export const TOP_LEVEL_CATEGORIES: IndustryCategory[] = [
  { key: 'finance',       label: 'Finance & Investment' },
  { key: 'technology',    label: 'Technology & Software' },
  { key: 'real_estate',   label: 'Real Estate' },
  { key: 'professional',  label: 'Professional Services' },
  { key: 'manufacturing', label: 'Manufacturing & Industrial' },
  { key: 'healthcare',    label: 'Healthcare & Life Sciences' },
  { key: 'energy',        label: 'Energy & Utilities' },
  { key: 'consumer',      label: 'Consumer & Retail' },
  { key: 'other',         label: 'Other' },
];

export const FINANCE_SUB_CATEGORIES: IndustryCategory[] = [
  { key: 'inv_trusts',      label: 'Investment Trusts' },
  { key: 'venture_growth',  label: 'Venture & Growth Capital' },
  { key: 'open_end_funds',  label: 'Open-Ended Funds' },
  { key: 'fund_management', label: 'Fund Management' },
  { key: 'market_infra',    label: 'Market Infrastructure' },
  { key: 'insurance',       label: 'Insurance' },
  { key: 'financial_other', label: 'Other Financial' },
];

const CATEGORY_BY_KEY: Record<string, IndustryCategory> = Object.fromEntries(
  [...TOP_LEVEL_CATEGORIES, ...FINANCE_SUB_CATEGORIES].map((c) => [c.key, c])
);

export function categoryLabel(key: string): string {
  return CATEGORY_BY_KEY[key]?.label ?? key;
}

// ---------------------------------------------------------------------------
// UK SIC 2007
// ---------------------------------------------------------------------------

const UK_SIC_FINANCE_SUB: Record<string, string> = {
  '64301': 'inv_trusts',
  '64303': 'venture_growth',
  '64304': 'open_end_funds',
  '64306': 'financial_other',
  '64999': 'financial_other',
  '66110': 'market_infra',
  '66120': 'market_infra',
  '66190': 'financial_other',
  '66220': 'insurance',
  '66290': 'insurance',
  '66300': 'fund_management',
};

const UK_SIC_DIVISION: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const add = (nums: number[], top: string) => {
    nums.forEach((n) => { map[String(n).padStart(2, '0')] = top; });
  };
  add([1, 2, 3], 'other');
  add([5, 6, 7, 8, 9], 'energy');
  for (let n = 10; n <= 33; n++) map[String(n).padStart(2, '0')] = 'manufacturing';
  add([35, 36, 37, 38, 39], 'energy');
  add([41, 42, 43], 'manufacturing');
  add([45, 46, 47], 'consumer');
  add([49, 50, 51, 52, 53], 'other');
  add([55, 56], 'consumer');
  add([58, 59, 60, 61, 62, 63], 'technology');
  add([64, 65, 66], 'finance');
  add([68], 'real_estate');
  add([69, 70, 71, 72, 73, 74, 75], 'professional');
  add([77, 78, 79, 80, 81, 82], 'professional');
  add([84, 85], 'other');
  add([86, 87, 88], 'healthcare');
  add([90, 91, 92, 93], 'consumer');
  add([94, 95, 96, 97, 98, 99], 'other');
  return map;
})();

// ---------------------------------------------------------------------------
// NAICS
// ---------------------------------------------------------------------------

const NAICS_SECTOR: Record<string, string> = {
  '11': 'other',
  '21': 'energy', '22': 'energy',
  '23': 'manufacturing',
  '31': 'manufacturing', '32': 'manufacturing', '33': 'manufacturing',
  '42': 'consumer',
  '44': 'consumer', '45': 'consumer',
  '48': 'other', '49': 'other',
  '51': 'technology',
  '52': 'finance',
  '53': 'real_estate',
  '54': 'professional', '55': 'professional', '56': 'professional',
  '61': 'other',
  '62': 'healthcare',
  '71': 'consumer', '72': 'consumer',
  '81': 'other',
  '92': 'other',
};

const NAICS_FINANCE_SUB: Record<string, string> = {
  '523110': 'market_infra',
  '523130': 'market_infra',
  '523140': 'market_infra',
  '523150': 'market_infra',
  '523160': 'market_infra',
  '523210': 'market_infra',
  '523910': 'venture_growth',
  '523920': 'fund_management',
  '523930': 'fund_management',
  '523940': 'fund_management',
  '523990': 'financial_other',
  '525110': 'open_end_funds',
  '525120': 'open_end_funds',
  '525190': 'open_end_funds',
  '525910': 'open_end_funds',
  '525920': 'inv_trusts',
  '525990': 'inv_trusts',
};

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

type Scheme = 'uk_sic_2007' | 'naics' | 'unknown';

function inferScheme(code: string): Scheme {
  const c = code.trim();
  if (!/^\d+$/.test(c)) return 'unknown';
  if (c.length === 5) return 'uk_sic_2007';
  if (c.length >= 2 && c.length <= 6) return 'naics';
  return 'unknown';
}

export interface Classification {
  top: string;
  sub: string | null;
}

export function classify(code: string, scheme?: Scheme): Classification {
  const c = code.trim();
  const sch: Scheme = scheme ?? inferScheme(c);

  if (sch === 'uk_sic_2007') {
    const top = UK_SIC_DIVISION[c.slice(0, 2)] ?? 'other';
    const sub = top === 'finance' ? UK_SIC_FINANCE_SUB[c] ?? null : null;
    return { top, sub };
  }

  if (sch === 'naics') {
    const top = NAICS_SECTOR[c.slice(0, 2)] ?? 'other';
    let sub: string | null = null;
    if (top === 'finance') {
      if (c.startsWith('524')) sub = 'insurance';
      else if (c.length >= 6) sub = NAICS_FINANCE_SUB[c.slice(0, 6)] ?? null;
      else if (c.startsWith('523')) sub = 'market_infra';
      else if (c.startsWith('525')) sub = 'inv_trusts';
      else if (c.startsWith('522')) sub = 'financial_other';
    }
    return { top, sub };
  }

  return { top: 'other', sub: null };
}

/** Pick a single representative category key for a row.
 *  Finance sub wins over top-level; non-"other" top wins over "other". */
export function primaryCategory(codes?: string[] | null): string | null {
  if (!codes || codes.length === 0) return null;
  let bestTop: string | null = null;
  for (const c of codes) {
    const { top, sub } = classify(c);
    if (sub) return sub;
    if (top !== 'other' && (bestTop === null || bestTop === 'other')) {
      bestTop = top;
    }
  }
  return bestTop ?? 'other';
}

/** Dedup+sort category keys for a code list. */
export function allCategoryKeys(codes?: string[] | null): string[] {
  if (!codes || codes.length === 0) return [];
  const seen = new Set<string>();
  for (const c of codes) {
    const { top, sub } = classify(c);
    seen.add(top);
    if (sub) seen.add(sub);
  }
  return [...seen].sort();
}

/** Group raw codes by the category they resolve into. Returns an array
 *  ordered the way `TOP_LEVEL_CATEGORIES` / `FINANCE_SUB_CATEGORIES` are
 *  declared, with finance subs grouped inside "finance". */
export function groupCodesByCategory(
  codes?: string[] | null
): Array<{ key: string; label: string; codes: string[] }> {
  if (!codes || codes.length === 0) return [];
  const buckets = new Map<string, string[]>();
  for (const c of codes) {
    const { top, sub } = classify(c);
    const key = sub ?? top;
    const list = buckets.get(key);
    if (list) list.push(c); else buckets.set(key, [c]);
  }
  const order = [
    ...FINANCE_SUB_CATEGORIES.map((c) => c.key),
    ...TOP_LEVEL_CATEGORIES.map((c) => c.key),
  ];
  const result: Array<{ key: string; label: string; codes: string[] }> = [];
  for (const key of order) {
    const list = buckets.get(key);
    if (list && list.length) {
      result.push({ key, label: categoryLabel(key), codes: [...new Set(list)].sort() });
    }
  }
  return result;
}
